import React, { useMemo, useState } from 'react';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

import { SearchInput, SpinnerOverlay } from '@components/common';

import { useQueryPortalFolders } from '@hooks/portal/folders/useQueryPortalFolders';

import { IPortalFolder } from '@interfaces/portal/folders';

import SelectFolderItem from './SelectFolderItem';
import { flattenFolders } from './utils';

import styles from './SelectFolderModal.module.scss';

interface ISelectFolderModalProps {
	projectId: string;
	selectFolder: (data: IPortalFolder) => void;
	disabledFolderId?: string;
}

const ROW_HEIGHT = 40;

const SelectFolderModal = ({
	disabledFolderId,
	projectId,
	selectFolder,
}: ISelectFolderModalProps) => {
	const [searchValue, setSearchValue] = useState('');

	const onSearchHandler = (value: string) => {
		setSearchValue(value);
	};

	const {
		portalFolders,
		isLoadingPortalFolders,
	} = useQueryPortalFolders({
		projectId,
		searchValue,
		milestoneIds: null,
	});

	const filteredFoldersList = searchValue
		? portalFolders.filter(folder => folder.isHighlighted)
		: portalFolders;

	const flatFolders = useMemo(() => {
		return flattenFolders(filteredFoldersList);
	}, [filteredFoldersList]);

	return (
		<div className={styles.selectFolderContainer}>
			{isLoadingPortalFolders && <SpinnerOverlay />}
			<div className={styles.searchInput}>
				<SearchInput
					isClearable
					onSearch={onSearchHandler}
					placeholder='Search by name'
					searchThreshold={0}
					value={searchValue}
				/>
			</div>
			<div className={styles.folderListContainer}>
				<AutoSizer>
					{({ height, width }: Size) => (
						<List
							height={height}
							itemCount={flatFolders.length}
							itemSize={ROW_HEIGHT}
							width={width}
						>
							{({ style, index }: ListChildComponentProps) => {
								const { folder, depth } = flatFolders[index];

								return (
									<div style={style}>
										<SelectFolderItem
											depth={depth}
											disabledFolderId={disabledFolderId}
											folder={folder}
											onSelect={selectFolder}
										/>
									</div>
								);
							}}
						</List>
					)}
				</AutoSizer>
			</div>
		</div>
	);
};

export default SelectFolderModal;
